'use client'
import React, { useCallback, useMemo } from 'react'
import { FixedButton } from '../../Buttons'
import styles from './styles.module.scss'
import { getCurrencySymbol } from '@l2p/apps-shared'
import { useTranslations } from 'next-intl'
import { format } from 'date-fns'
import { BonusCardStatus, BonusCardDto } from '@l2p/profile-api'

interface BonusCardProps extends BonusCardDto {
  cancelBonus: (bonus: BonusCardDto) => void
  activateBonus: (bonus: BonusCardDto) => void
  isActivateSubmitting: boolean
  bonusToDirectActivation: BonusCardDto | null
}
const BonusCard: React.FC<BonusCardProps> = ({
  isActivateSubmitting,
  cancelBonus,
  activateBonus,
  bonusToDirectActivation,
  ...bonus
}) => {
  const t = useTranslations('translation')

  const onClickHandler = useCallback(() => {
    if (!bonus.id) return
    if (bonus.status === BonusCardStatus.Pending) activateBonus(bonus)
    else if (bonus.status === BonusCardStatus.InProgress) cancelBonus(bonus)
  }, [bonus, activateBonus, cancelBonus])

  const formatAmount = useMemo(() => {
    if (bonus.status === BonusCardStatus.InProgress)
      return `${getCurrencySymbol(bonus.currency)} ${Number(
        bonus.balance,
      ).toFixed(2)} / ${getCurrencySymbol(bonus.currency)} ${Number(
        bonus.startBalance,
      ).toFixed(2)}`
    return `${getCurrencySymbol(bonus.currency)} ${Number(
      bonus.startBalance,
    ).toFixed(2)}`
  }, [bonus.currency, bonus.status, bonus.startBalance, bonus.balance])

  return (
    <div className={styles.container}>
      <div className={styles.title}>{bonus.bonus?.title}</div>
      <div className={styles.properties}>
        <div>
          <div className={styles.property}>{t('amount')}</div>
          <div className={styles.value}>{formatAmount}</div>
        </div>
        <div>
          <div className={styles.property}>{t('amountWageringRequired')}</div>
          <div className={styles.value}>{`${getCurrencySymbol(
            bonus.currency,
          )} ${Number(bonus.wageringBalance).toFixed(2)}`}</div>
        </div>
        {bonus.createdAt && (
          <div>
            <div className={styles.property}>{t('dateReceived')}</div>
            <div className={styles.value}>
              {format(new Date(bonus.createdAt), 'dd-LL-yy')}
            </div>
          </div>
        )}
        {bonus.expireDate && (
          <div>
            <div className={styles.property}>{t('expireDate')}</div>
            <div className={styles.value}>
              {format(new Date(bonus.expireDate), 'dd-LL-yy')}
            </div>
          </div>
        )}
      </div>
      <FixedButton
        onClick={onClickHandler}
        color={bonus.status === BonusCardStatus.Pending ? 'green' : 'red'}
        width="100%"
        maxWidth="100%"
        text="upper"
        loading={
          isActivateSubmitting && bonusToDirectActivation?.id === bonus?.id
        }
      >
        {bonus.status === BonusCardStatus.Pending
          ? t('activateBonus')
          : t('cancelBonus')}
      </FixedButton>
    </div>
  )
}

export default BonusCard
